/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { numberToWords } from "../components/printTemplate";

const createRow = () => ({
  fullName: "",
  rank: "",
  coeff: "",
  prodQty: "",
  prodMoney: "",
  timeDays: "",
  timeMoney: "",
  leaveDays: "",
  leaveMoney: "",
  allowanceSalary: "",
  allowanceOther: "",
  advance: "",
  bhxh: "",
  deductOther: "",
  tax: "",
});

function SalaryTable({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const [headerInfo, setHeaderInfo] = useState({
    unit: values.unit || "",
    department: values.department || "",
    docNumber: values.docNumber || "",
    day: values.day || today.getDate(),
    month: values.month || currentMonth,
    year: values.year || currentYear,
  });

  const [localRows, setLocalRows] = useState(() => {
    if (values.salaryRows && Array.isArray(values.salaryRows) && values.salaryRows.length > 0) {
      return values.salaryRows;
    }
    return [createRow()];
  });

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) onChangeValue(field, val);
  };

  useEffect(() => {
    if (values.salaryRows && values.salaryRows.length > 0) {
      setLocalRows(values.salaryRows);
      setHeaderInfo({
        unit: values.unit || "",
        department: values.department || "",
        docNumber: values.docNumber || "",
        day: values.day || today.getDate(),
        month: values.month || currentMonth,
        year: values.year || currentYear,
      });
    }
  }, [
    values.salaryRows,
    values.unit,
    values.department,
    values.docNumber,
    values.day,
    values.month,
    values.year,
  ]);

  const handleChangeRow = (index, field, value) => {
    const newRows = [...localRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setLocalRows(newRows);
  };

  const addRow = () => {
    setLocalRows([...localRows, createRow()]);
  };

  const removeRow = (index) => {
    if (localRows.length <= 1) return;
    const updated = localRows.filter((_, i) => i !== index);
    setLocalRows(updated);
  };

  const calculatedRows = useMemo(() => {
    return localRows.map((r) => {
      const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

      const totalGross =
        num(r.prodMoney) +
        num(r.timeMoney) +
        num(r.leaveMoney) +
        num(r.allowanceSalary) +
        num(r.allowanceOther);
      const totalDeduct = num(r.bhxh) + num(r.deductOther) + num(r.tax);
      const netPay = totalGross - num(r.advance) - totalDeduct;

      return { ...r, totalGross, totalDeduct, netPay };
    });
  }, [localRows]);

  const totals = useMemo(() => {
    return calculatedRows.reduce(
      (acc, curr) => ({
        prodMoney: acc.prodMoney + (Number(curr.prodMoney) || 0),
        timeMoney: acc.timeMoney + (Number(curr.timeMoney) || 0),
        leaveMoney: acc.leaveMoney + (Number(curr.leaveMoney) || 0),
        allowanceSalary: acc.allowanceSalary + (Number(curr.allowanceSalary) || 0),
        allowanceOther: acc.allowanceOther + (Number(curr.allowanceOther) || 0),
        totalGross: acc.totalGross + curr.totalGross,
        advance: acc.advance + (Number(curr.advance) || 0),
        bhxh: acc.bhxh + (Number(curr.bhxh) || 0),
        deductOther: acc.deductOther + (Number(curr.deductOther) || 0),
        tax: acc.tax + (Number(curr.tax) || 0),
        totalDeduct: acc.totalDeduct + curr.totalDeduct,
        netPay: acc.netPay + curr.netPay,
      }),
      {
        prodMoney: 0,
        timeMoney: 0,
        leaveMoney: 0,
        allowanceSalary: 0,
        allowanceOther: 0,
        totalGross: 0,
        advance: 0,
        bhxh: 0,
        deductOther: 0,
        tax: 0,
        totalDeduct: 0,
        netPay: 0,
      }
    );
  }, [calculatedRows]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("salaryRows", calculatedRows);
      onChangeValue("totals", totals);
    }
  }, [calculatedRows, totals]);

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "250px",
      fontSize: "14px",
      fontFamily: "inherit",
      fontWeight: "bold",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "100px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "20px 0" },
    titleMain: { fontSize: "20px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "12px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
      height: "40px",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px", position: "relative" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 4px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "30px",
    },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n ? new Intl.NumberFormat("vi-VN").format(n) : "");

  const handleMonthChange = (val) => {
    if (val === "" || /^[0-9\b]+$/.test(val)) {
      handleHeaderChange("month", val);
    }
  };

  const handleMonthBlur = () => {
    let m = parseInt(headerInfo.month, 10);
    if (!m || m < 1) m = 1;
    if (m > 12) m = 12;
    handleHeaderChange("month", m.toString().padStart(2, "0"));
  };

  const handleYearChange = (val) => {
    if (val === "" || /^[0-9\b]+$/.test(val)) {
      if (val.length <= 4) {
        handleHeaderChange("year", val);
      }
    }
  };

  const handleYearBlur = () => {
    let y = parseInt(headerInfo.year, 10);
    if (!y || y < 1900) y = currentYear;
    handleHeaderChange("year", y.toString());
  };

  return (
    <SoftBox style={s.container}>
      <div style={s.headerTop}>
        <div style={{ width: "60%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{"  "}
            <input
              disabled={containView}
              style={s.inputDotted}
              value={headerInfo.unit}
              onChange={(e) => handleHeaderChange("unit", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Bộ phận:{"  "}
            <input
              disabled={containView}
              style={s.inputDotted}
              value={headerInfo.department}
              onChange={(e) => handleHeaderChange("department", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "40%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số: 01 - LĐTL</div>
          <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      <div style={s.titleBox}>
        <h2 style={s.titleMain}>BẢNG THANH TOÁN TIỀN LƯƠNG</h2>
        <div style={{ fontStyle: "italic", marginTop: 5 }}>
          Tháng{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "30px", textAlign: "center" }}
            value={headerInfo.month}
            onChange={(e) => handleMonthChange(e.target.value)}
            onBlur={handleMonthBlur}
            placeholder="MM"
          />{" "}
          năm{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "50px", textAlign: "center" }}
            value={headerInfo.year}
            onChange={(e) => handleYearChange(e.target.value)}
            onBlur={handleYearBlur}
            placeholder="YYYY"
          />
        </div>
        <div style={{ textAlign: "right", marginTop: 10 }}>
          Số:{"  "}
          <input
            disabled={containView}
            style={s.inputDottedSmall}
            value={headerInfo.docNumber}
            onChange={(e) => handleHeaderChange("docNumber", e.target.value)}
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #ccc" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={2} style={s.th}>
                STT
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "140px" }}>
                Họ và tên
              </th>
              <th rowSpan={2} style={s.th}>
                Ngạch bậc
                <br />
                lương, CV
              </th>
              <th rowSpan={2} style={s.th}>
                Hệ
                <br />
                số
              </th>
              <th colSpan={2} style={s.th}>
                Lương sản phẩm
              </th>
              <th colSpan={2} style={s.th}>
                Lương thời gian
              </th>
              <th colSpan={2} style={s.th}>
                Nghỉ việc
                <br />
                hưởng % lương
              </th>
              <th rowSpan={2} style={s.th}>
                Phụ cấp
                <br />
                lương
              </th>
              <th rowSpan={2} style={s.th}>
                Phụ cấp
                <br />
                khác
              </th>
              <th rowSpan={2} style={s.th}>
                Tổng
                <br />
                số
              </th>
              <th rowSpan={2} style={s.th}>
                Tạm
                <br />
                ứng
              </th>
              <th colSpan={3} style={s.th}>
                Các khoản khấu trừ
              </th>
              <th rowSpan={2} style={s.th}>
                Thực
                <br />
                lĩnh
              </th>
              <th rowSpan={2} style={s.th}>
                Ký
                <br />
                nhận
              </th>
              {!containView && (
                <th rowSpan={2} style={{ ...s.th, width: "30px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th style={s.th}>Số SP</th>
              <th style={s.th}>Thành tiền</th>
              <th style={s.th}>Số công</th>
              <th style={s.th}>Thành tiền</th>
              <th style={s.th}>Số công</th>
              <th style={s.th}>Thành tiền</th>
              <th style={s.th}>BHXH</th>
              <th style={s.th}>Khác</th>
              <th style={s.th}>Thuế</th>
            </tr>
            <tr style={{ backgroundColor: "#fafafa" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
              <th style={s.th}>9</th>
              <th style={s.th}>10</th>
              <th style={s.th}>11</th>
              <th style={s.th}>12</th>
              <th style={s.th}>13</th>
              <th style={s.th}>14</th>
              <th style={s.th}>15</th>
              <th style={s.th}>16</th>
              <th style={s.th}>C</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {calculatedRows.map((row, i) => (
              <tr key={i}>
                <td style={{ ...s.td, textAlign: "center" }}>{i + 1}</td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableText}
                    value={row.fullName || ""}
                    onChange={(e) => handleChangeRow(i, "fullName", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={{ ...s.inputTable, textAlign: "center" }}
                    value={row.rank || ""}
                    onChange={(e) => handleChangeRow(i, "rank", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={{ ...s.inputTable, textAlign: "center" }}
                    value={row.coeff || ""}
                    onChange={(e) => handleChangeRow(i, "coeff", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.prodQty || ""}
                    onChange={(e) => handleChangeRow(i, "prodQty", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.prodMoney || ""}
                    onChange={(e) => handleChangeRow(i, "prodMoney", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.timeDays || ""}
                    onChange={(e) => handleChangeRow(i, "timeDays", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.timeMoney || ""}
                    onChange={(e) => handleChangeRow(i, "timeMoney", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.leaveDays || ""}
                    onChange={(e) => handleChangeRow(i, "leaveDays", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.leaveMoney || ""}
                    onChange={(e) => handleChangeRow(i, "leaveMoney", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.allowanceSalary || ""}
                    onChange={(e) => handleChangeRow(i, "allowanceSalary", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.allowanceOther || ""}
                    onChange={(e) => handleChangeRow(i, "allowanceOther", e.target.value)}
                  />
                </td>
                <td style={{ ...s.td, backgroundColor: "#f9f9f9" }}>
                  <span style={s.totalText}>{fmt(row.totalGross)}</span>
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.advance || ""}
                    onChange={(e) => handleChangeRow(i, "advance", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.bhxh || ""}
                    onChange={(e) => handleChangeRow(i, "bhxh", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.deductOther || ""}
                    onChange={(e) => handleChangeRow(i, "deductOther", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.tax || ""}
                    onChange={(e) => handleChangeRow(i, "tax", e.target.value)}
                  />
                </td>
                <td style={{ ...s.td, backgroundColor: "#e8f5e9" }}>
                  <span style={s.totalText}>{fmt(row.netPay)}</span>
                </td>
                <td style={s.td}></td>
                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", backgroundColor: "#eee" }}>
              <td colSpan={5} style={{ ...s.td, textAlign: "center" }}>
                Cộng
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.prodMoney)}</span>
              </td>
              <td style={s.td}></td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.timeMoney)}</span>
              </td>
              <td style={s.td}></td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.leaveMoney)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.allowanceSalary)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.allowanceOther)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.totalGross)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.advance)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.bhxh)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.deductOther)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(totals.tax)}</span>
              </td>
              <td style={{ ...s.td, color: "darkgreen", fontSize: "14px" }}>
                <span style={s.totalText}>{fmt(totals.netPay)}</span>
              </td>
              <td style={{ ...s.td, textAlign: "center" }}>X</td>
              <td style={s.td}></td>
            </tr>
          </tbody>
        </table>
      </div>
      {!containView && (
        <SoftBox mt={4} display="flex" justifyContent="flex-end">
          <SoftButton variant="outlined" color="dark" onClick={addRow}>
            THÊM DÒNG
          </SoftButton>
        </SoftBox>
      )}
      <div style={{ marginTop: "20px", fontStyle: "italic" }}>
        Tổng số tiền (viết bằng chữ):{" "}
        <span style={{ fontWeight: "bold" }}>{numberToWords(totals.netPay)}</span>
        ......................................................................................
      </div>
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "25%" }}>
          <b>Người lập bảng</b>
          <br />
          <i style={{ fontSize: "13px" }}>(Ký, họ tên)</i>
        </div>
        <div style={{ width: "25%" }}>
          <b>Kế toán trưởng</b>
          <br />
          <i style={{ fontSize: "13px" }}>(Ký, họ tên)</i>
        </div>
        <div style={{ width: "30%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày {headerInfo.day} tháng {headerInfo.month} năm {headerInfo.year}
          </div>
          <b>Giám đốc</b>
          <br />
          <i style={{ fontSize: "13px" }}>(Ký, họ tên)</i>
        </div>
      </div>
      <div style={{ marginTop: "20px", fontStyle: "italic", fontSize: "13px" }}>
        <b>Ghi chú:</b> <span style={{ textDecoration: "underline" }}>Tùy</span> theo đặc điểm hoạt
        động sản xuất kinh doanh và yêu cầu quản lý của đơn vị mình, doanh nghiệp được xây dựng,
        thiết kế biểu mẫu chứng từ kế toán.
      </div> */}
    </SoftBox>
  );
}

SalaryTable.propTypes = {
  formData: PropTypes.object,
};

export default SalaryTable;
